import { useState, type FormEvent } from 'react';
import { ApiError, requestSignInCode } from '../lib/api.js';
import { cleanCode, cleanEmail, isCompleteCode } from '../lib/codeSignIn.js';
import { verifyEmailCode } from '../lib/supabase.js';

/**
 * The only sign-in (D-066): an email, then the six-digit code it receives.
 * The code is asked for through our API, which sends one only to an account's
 * address or an active Jobber user's, and answers alike either way. Supabase
 * keeps the session once the code is right; whatever page holds this form then
 * shows what it guards. A wrong address or a lost code starts over by reloading
 * the page: no resend or change-address controls (Dakota, 2026-10-01).
 */
export function CodeSignIn({ title }: { title: string }) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'good' | 'bad'; text: string } | null>(null);

  const sendCode = async (event: FormEvent) => {
    event.preventDefault();
    const address = cleanEmail(email);
    if (!address) return;
    setBusy(true);
    setMessage(null);
    try {
      await requestSignInCode(address);
      setEmail(address);
      setCode('');
      setStep('code');
    } catch (err: unknown) {
      setMessage({ kind: 'bad', text: err instanceof ApiError ? err.message : 'Could not send a code. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    if (!isCompleteCode(code)) return;
    setBusy(true);
    setMessage(null);
    try {
      await verifyEmailCode(email, code);
    } catch {
      setMessage({ kind: 'bad', text: 'That code is wrong or has expired.' });
      setBusy(false);
    }
  };

  return (
    <div className="signin__card">
      <h1 className="brand__name">UnitedOS</h1>
      <p className="text-muted">{title}</p>
      {step === 'email' ? (
        <form className="stack" onSubmit={(e) => void sendCode(e)}>
          <input
            className="field"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            aria-label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <button type="submit" className="button button--primary button--wide" disabled={busy}>
            {busy ? 'Sending…' : 'Email me a code'}
          </button>
        </form>
      ) : (
        <form className="stack" onSubmit={(e) => void verify(e)}>
          <label className="stack">
            <span title="It arrives only if this address can sign in, and works for 24 hours">Code sent to {email}. Wrong address or no code? Reload this page.</span>
            <input
              className="field code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(cleanCode(e.target.value))}
              required
              autoFocus
            />
          </label>
          <button type="submit" className="button button--primary button--wide" disabled={busy || !isCompleteCode(code)}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}
      {message && <p className={message.kind === 'good' ? 'text-good' : 'text-bad'}>{message.text}</p>}
    </div>
  );
}
